import { ProjectStageResponse, ProjectStageRequest } from './../../entities/project/ProjectStageResponse';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap, map } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { ProjectStageAPI } from '@business/api/projectStage';
import { BoardId } from '@business/entities/project';
import { R } from '@core/utils/r';

export class ProjectStageBloc extends BaseBloc {
  private readonly projectStageRepo = new Repository<ProjectStageResponse>({
    getItemId: stage => stage.id,
  });

  readonly projectStages$ = this.projectStageRepo.items$;
  readonly selectProjectStage = this.projectStageRepo.selectItem;

  readonly stages$ = this.projectStageRepo.items$;
  readonly stagesByBoard$ = (boardId: BoardId) =>
    this.projectStageRepo.items$.pipe(
      map(items =>
        R.pipe(
          items,
          R.ops.filter(i => i.board.id === boardId),
          R.ops.sortBy(i => i.menuOrder),
        ),
      ),
    );

  onReset() {
    this.projectStageRepo.reset();
  }

  fetchProjectStages = () => {
    return ProjectStageAPI.fetchProjectStages().pipe(
      this.projectStageRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_project_stage_error') }),
    );
  };

  fetchProjectStage = (id: string) => {
    return ProjectStageAPI.fetchProjectStage(id).pipe(
      this.projectStageRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_project_stage_error') }),
    );
  };

  createProjectStage = (stage: ProjectStageRequest) => {
    return ProjectStageAPI.createProjectStage(stage).pipe(
      this.projectStageRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_project_stage'),
      }),
    );
  };

  updateProjectStage = (id: string, stage: ProjectStageRequest) => {
    return ProjectStageAPI.updateProjectStage(id, stage).pipe(
      this.projectStageRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project_stage'),
      }),
    );
  };

  deleteProjectStage = (id: string) => {
    return showDeleteConfirmation('Delete Stage', 'Do you really want to remove this stage?').pipe(
      switchMap(() => ProjectStageAPI.deleteProjectStage(id)),
      this.projectStageRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_project_stage'),
      }),
    );
  };
}
